import { FC, useState } from 'react';
import { CheckIcon } from '@chakra-ui/icons';
import {
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  GetTreatyUserDocument,
  TreatyUserDetailFragment,
  useResetMfaMutation,
} from '../../../../graphql/generated';
import { Failed } from '../failed';
import { Border } from '../read-only-view';
import { Success } from '../success';

type MFAProps = {
  treatyUser: TreatyUserDetailFragment;
  isLoading: boolean;
};

export const MFA: FC<MFAProps> = ({ treatyUser, isLoading }) => {
  const [showConfirmReset, setShowConfirmReset] = useState<boolean>(false);
  const [showSaved, setShowSaved] = useState<boolean>(false);
  const [showFailed, setShowFailed] = useState<boolean>(false);

  const [resetMFA, { loading }] = useResetMfaMutation({
    refetchQueries: [GetTreatyUserDocument],
    awaitRefetchQueries: true,
  });

  const handleToggleResetConfirmation = () => {
    // Clear any previous messages
    setShowSaved(false);
    setShowFailed(false);

    // Toggle the confirmation message
    setShowConfirmReset(!showConfirmReset);
  };

  const handleResetMFA = async () => {
    try {
      await resetMFA({
        variables: {
          userId: treatyUser.id,
        },
      });
      setShowSaved(true);
    } catch {
      setShowFailed(true);
    } finally {
      setShowConfirmReset(false);
    }
  };

  const showResetButton = treatyUser.hasMFAEnabled && !showConfirmReset;

  return (
    <Border pb={4}>
      <Flex justify="space-between" align="baseline">
        <HStack spacing={8} minH={10}>
          <Text fontWeight="semibold">Multi-Factor Authentication</Text>

          {isLoading && <Spinner color="gray.500" size="xs" />}
          {!isLoading && <MFAStatus hasMFAEnabled={treatyUser.hasMFAEnabled} />}
        </HStack>

        {!isLoading && showResetButton && (
          <Button
            onClick={handleToggleResetConfirmation}
            colorScheme="blue"
            textDecoration="underline"
            variant="ghost"
            data-testid="reset-mfa-button"
          >
            Reset MFA
          </Button>
        )}
      </Flex>

      {showConfirmReset && (
        <Stack data-testid="confirm-reset-section">
          <Text color="gray.600">
            Reset Multi-Factor Authentication for this user?
          </Text>
          <ButtonGroup py={2}>
            <Button
              onClick={handleResetMFA}
              isLoading={loading}
              colorScheme="blue"
              data-testid="confirm-reset-mfa-button"
            >
              Reset MFA
            </Button>
            <Button
              onClick={() => setShowConfirmReset(false)}
              disabled={loading}
              colorScheme="blue"
              variant="ghost"
              data-testid="cancel-reset-mfa-button"
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Stack>
      )}

      {showSaved && (
        <Success
          title="MFA has been reset"
          description="The user will be prompted to set up MFA the next time they login"
          data-testid="reset-mfa-success-message"
        />
      )}
      {showFailed && (
        <Failed title="Reset failed" data-testid="reset-mfa-failed-message" />
      )}
    </Border>
  );
};

const MFAStatus: FC<{ hasMFAEnabled: boolean }> = ({ hasMFAEnabled }) => {
  if (hasMFAEnabled) {
    return (
      <Text color="gray.500" data-testid="mfa-enabled">
        <CheckIcon mr={2} color="green.500" />
        Enabled
      </Text>
    );
  }

  return (
    <Text color="gray.500" data-testid="mfa-pending">
      Pending
    </Text>
  );
};
